import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "../../../services/apiClient";
import API_ENDPOINTS from "../../../config/apiEndpoints";
import { useUserData } from "../../../hooks/useUserData";
import Dropdown from "../../common/dropDown";
import useInfoAlert from "../../../hooks/useInfoAlert";
import InfoAlert from "../../common/InfoAlert";
import {
  CAPA_ISF_TEMPLATE_CATEGORIES,
  CAPA_RISK_OPTIONS,
  normalizeIsfTemplateCategoryForApi,
} from "./capaConstants";
import { fetchCapaConfig, saveCapa } from "./capaService";
import { mapCapaStaffOptions } from "./capaStaffUtils";
import { useCapaAccess } from "./capaAccess";
import {
  resolveCapaModuleDropdownOptions,
  shouldUseSessionCapaModules,
  toCapaModuleOption,
  capaModuleDropdownOptionsWithAll,
  sessionModuleOptionForStoredCapa,
  getCapaEntityUiMode,
  resolveCapaLinkSlugsForDropdownValue,
  validateCapaCreateModuleSelection,
  buildCapaCreateModulePayload,
} from "./capaSessionModules";
import { INITIAL_CAPA_STATUS } from "./capaWorkflow";
import TextEditor from "../../common/TextEditor/TextEditor";
import { stripHtml } from "../../../utils/avatarUtils";
import "./CapaCreate.scss";

function optionFromValue(options, value) {
  const v = value == null ? "" : String(value);
  return options.find((o) => o.value === v) || options[0];
}

const EMPTY_CONTEXT = {
  locked: false,
  module: undefined,
  entityType: undefined,
  entityId: undefined,
  entityLabel: undefined,
  studyId: undefined,
  templateCategory: undefined,
};

/**
 * Shared Create CAPA form — full page (`variant="page"`) or embedded modal (`variant="modal"`).
 * @param {{ locked?: boolean, module?: string, entityType?: string, entityId?: string, entityLabel?: string, studyId?: string, templateCategory?: string }} context
 */
const CapaCreateForm = ({
  context = EMPTY_CONTEXT,
  variant = "page",
  onCancel,
  onSaved,
}) => {
  const { userData } = useUserData();
  const { canAddCapa: canCreateCapa } = useCapaAccess();
  const { alertState, showAlert, hideAlert } = useInfoAlert();
  const ctx = context || EMPTY_CONTEXT;
  const isModal = variant === "modal";

  const [studies, setStudies] = useState([]);
  const [staffOptions, setStaffOptions] = useState([]);
  const [documentOptions, setDocumentOptions] = useState([]);
  const [loadingStudies, setLoadingStudies] = useState(false);
  const [loadingStaff, setLoadingStaff] = useState(false);
  const [loadingDocs, setLoadingDocs] = useState(false);

  const [moduleOptions, setModuleOptions] = useState([]);
  const [moduleOption, setModuleOption] = useState(null);
  const [selectedStudy, setSelectedStudy] = useState(null);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [isfStudy, setIsfStudy] = useState(null);
  const [isfCategory, setIsfCategory] = useState(CAPA_ISF_TEMPLATE_CATEGORIES[0]);

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [sourceType, setSourceType] = useState(null);
  const [sourceTypeOptions, setSourceTypeOptions] = useState([]);
  const [sourceRef, setSourceRef] = useState("");
  const [riskLevel, setRiskLevel] = useState(CAPA_RISK_OPTIONS[1]);

  const contextLocked = ctx.locked;
  const selectedModuleValue = String(moduleOption?.value || "");

  const linkSlugs = useMemo(
    () => resolveCapaLinkSlugsForDropdownValue(selectedModuleValue, userData),
    [selectedModuleValue, userData],
  );

  const entityUiMode = useMemo(() => getCapaEntityUiMode(linkSlugs), [linkSlugs]);

  const applyContext = useCallback(() => {
    if (!ctx.locked) return;
    const sessionMods = Array.isArray(userData?.Modules) ? userData.Modules : [];
    let mod =
      sessionMods.length > 0
        ? sessionModuleOptionForStoredCapa({ Module: ctx.module }, sessionMods)
        : null;
    if (!mod) {
      mod =
        moduleOptions.find((m) => m.value === ctx.module) ||
        toCapaModuleOption(ctx.module) ||
        moduleOptions[0] ||
        null;
    }
    setModuleOption(mod);
    if (ctx.module === "study_management" && ctx.entityType === "study") {
      setSelectedStudy({
        value: ctx.entityId,
        label: ctx.entityLabel || ctx.entityId,
      });
    } else if (ctx.module === "user_management" && ctx.entityType === "staff") {
      setSelectedStaff({
        value: ctx.entityId,
        label: ctx.entityLabel || ctx.entityId,
      });
    } else if (ctx.module === "isf" && ctx.entityType === "document") {
      setSelectedDocument({
        value: ctx.entityId,
        label: ctx.entityLabel || ctx.entityId,
      });
      if (ctx.studyId) {
        setIsfStudy({
          value: ctx.studyId,
          label: ctx.studyLabel || ctx.studyId,
        });
      }
      if (ctx.templateCategory) {
        const normalized = normalizeIsfTemplateCategoryForApi(ctx.templateCategory);
        const cat = CAPA_ISF_TEMPLATE_CATEGORIES.find(
          (c) => c.value === normalized || c.value === ctx.templateCategory,
        );
        if (cat) setIsfCategory(cat);
      }
    }
  }, [ctx, moduleOptions, userData?.Modules]);

  useEffect(() => {
    applyContext();
  }, [applyContext]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!userData?.OrganizationId) return;
      try {
        const cfg = await fetchCapaConfig(userData);
        const moduleFromApi = Array.isArray(cfg?.modules) ? cfg.modules : [];
        const mappedModules = capaModuleDropdownOptionsWithAll(
          resolveCapaModuleDropdownOptions(userData, moduleFromApi),
        );
        if (process.env.NODE_ENV === "development") {
          console.info("[CAPA Create] Modules from session (userData.Modules):", userData?.Modules);
          console.info("[CAPA Create] Using session for dropdown?", shouldUseSessionCapaModules(userData));
          console.info("[CAPA Create] CAPA config modules fallback slugs:", moduleFromApi);
          console.info("[CAPA Create] Resolved module dropdown options:", mappedModules);
        }
        const fromApi = Array.isArray(cfg?.sourceTypes) ? cfg.sourceTypes : [];
        const mapped = fromApi
          .map((s) => String(s || "").trim())
          .filter(Boolean)
          .map((v) => ({ value: v, label: v }));
        if (!cancelled) {
          setModuleOptions(mappedModules);
          setModuleOption((prev) =>
            mappedModules.length > 0
              ? optionFromValue(mappedModules, prev?.value || "")
              : null,
          );
        }
        if (!cancelled && mapped.length > 0) {
          setSourceTypeOptions(mapped);
          setSourceType((prev) => optionFromValue(mapped, prev?.value || ""));
        }
      } catch {
        /* keep options empty when config unavailable */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userData]);

  const loadStudies = useCallback(async () => {
    if (!userData?.OrganizationId || !userData?.StaffId) return;
    setLoadingStudies(true);
    try {
      const response = await api.post(API_ENDPOINTS.STUDY.GET_ALL_STUDIES, {
        OrganizationId: userData.OrganizationId,
        StaffIdRef: userData.StaffId,
        CreatedBy: userData.StaffId,
        Action: "GetStudyOrscheduledlist",
        AccessCode: "",
        Password: "",
      });
      if (response?.success === 1) {
        const rows = Array.isArray(response.data) ? response.data : [];
        const mapped = rows
          .map((row) => {
            const id = row?.StudyId ?? row?.StudyIdRef ?? row?.studyId ?? "";
            const num = row?.StudyNumber || row?.StudyCode || "";
            const name = row?.StudyName || "";
            const label = num && name ? `${num} — ${name}` : name || num || String(id);
            return { value: id, label };
          })
          .filter((o) => o.value);
        setStudies(mapped);
      }
    } finally {
      setLoadingStudies(false);
    }
  }, [userData]);

  const loadStaff = useCallback(async () => {
    if (!userData?.OrganizationId || !userData?.StaffId) return;
    setLoadingStaff(true);
    try {
      const response = await api.post(API_ENDPOINTS.USER.GET_USERLIST, {
        OrganizationId: userData.OrganizationId,
        StaffId: userData.StaffId,
        Action: "Get",
        AccessCode: "",
        Password: "",
        HaveProfileViewRights: 1,
        HaveProfileEditRights: 0,
        HaveDelegationLogViewRights: 1,
        HaveDelegationLogEditRights: 0,
        HavingLineManagerRights: 1,
        HaveStudyTaskArchiveRights: 0,
        CreatedBy: userData.StaffId,
      });
      if (response?.success === 1) {
        const list = response.data?.UsersActivity || [];
        setStaffOptions(mapCapaStaffOptions(list));
      }
    } finally {
      setLoadingStaff(false);
    }
  }, [userData]);

  const loadIsfDocuments = useCallback(
    async (studyId, templateCategoryValue) => {
      const category = normalizeIsfTemplateCategoryForApi(templateCategoryValue);
      if (!userData?.OrganizationId || !studyId || !category) {
        setDocumentOptions([]);
        return;
      }
      setLoadingDocs(true);
      try {
        const response = await api.post(API_ENDPOINTS.ISF.GET_STUDY_FOLDERS, {
          OrganizationIdRef: userData.OrganizationId,
          ActionType: "GetAllFolder",
          StaudyIdRef: studyId,
          TemplateId: "",
          TemplateName: "",
          TemplateCategory: category,
        });
        const payload = response?.data != null ? response.data : response;
        const folderDocuments = Array.isArray(payload?.FolderDocuments)
          ? payload.FolderDocuments
          : [];
        const active = folderDocuments.filter(
          (item) => item.FileStatus === 0 || item.FileStatus === "0",
        );
        setDocumentOptions(
          active.map((item) => ({
            value: item.DocumentIdRef,
            label: item.FileName || item.DocumentIdRef,
          })),
        );
      } finally {
        setLoadingDocs(false);
      }
    },
    [userData],
  );

  useEffect(() => {
    if (!userData || contextLocked) return;
    loadStudies();
    loadStaff();
  }, [userData, contextLocked, loadStudies, loadStaff]);

  useEffect(() => {
    if (contextLocked || entityUiMode !== "isf") return;
    if (isfStudy?.value && isfCategory?.value) {
      loadIsfDocuments(isfStudy.value, isfCategory.value);
    } else {
      setDocumentOptions([]);
      setSelectedDocument(null);
    }
  }, [
    contextLocked,
    entityUiMode,
    isfStudy,
    isfCategory,
    loadIsfDocuments,
  ]);

  useEffect(() => {
    if (!contextLocked || entityUiMode !== "isf" || ctx.entityType !== "document")
      return;
    if (ctx.studyId && ctx.templateCategory) {
      loadIsfDocuments(ctx.studyId, ctx.templateCategory);
    }
  }, [contextLocked, ctx, entityUiMode, loadIsfDocuments]);

  const handleModuleChange = (opt) => {
    if (opt?.disabled) return;
    setModuleOption(opt || moduleOptions[0] || null);
    setSelectedStudy(null);
    setSelectedStaff(null);
    setSelectedDocument(null);
    setIsfStudy(null);
    setIsfCategory(CAPA_ISF_TEMPLATE_CATEGORIES[0]);
  };

  const resetCreateForm = useCallback(() => {
    setTitle("");
    setDescription("");
    setSourceType(sourceTypeOptions[0] || null);
    setSourceRef("");
    setRiskLevel(CAPA_RISK_OPTIONS[1]);
    if (!contextLocked) {
      setModuleOption(moduleOptions[0] || null);
      setSelectedStudy(null);
      setSelectedStaff(null);
      setSelectedDocument(null);
      setIsfStudy(null);
      setIsfCategory(CAPA_ISF_TEMPLATE_CATEGORIES[0]);
    }
  }, [contextLocked, moduleOptions, sourceTypeOptions]);

  const handleSave = () => {
    if (!title.trim()) {
      showAlert({
        title: "Validation",
        message: "Please enter a title.",
        type: "warning",
      });
      return;
    }
    if (!stripHtml(description)) {
      showAlert({
        title: "Validation",
        message: "Please enter a description.",
        type: "warning",
      });
      return;
    }
    if (!contextLocked) {
      const validation = validateCapaCreateModuleSelection({
        selectedValue: selectedModuleValue,
        userData,
        selectedStudy,
        selectedStaff,
        selectedDocument,
        isfStudy,
        isfCategory,
      });
      if (!validation.ok) {
        showAlert({
          title: "Validation",
          message: validation.message || "Please complete module fields.",
          type: "warning",
        });
        return;
      }
    }

    const moduleCtx = contextLocked
      ? {
          module: ctx.module,
          entityType: ctx.entityType,
          entityId: ctx.entityId,
          entityLabel: ctx.entityLabel,
          studyIdForIsf:
            ctx.module === "isf" ? ctx.studyId : undefined,
          templateCategoryForIsf:
            ctx.module === "isf"
              ? normalizeIsfTemplateCategoryForApi(ctx.templateCategory)
              : undefined,
        }
      : buildCapaCreateModulePayload({
          selectedValue: selectedModuleValue,
          userData,
          selectedStudy,
          selectedStaff,
          selectedDocument,
          isfStudy,
          isfCategory,
        });

    const payload = {
      Status: INITIAL_CAPA_STATUS,
      module: moduleCtx.module,
      entityType: moduleCtx.entityType,
      entityId: moduleCtx.entityId,
      entityLabel: moduleCtx.entityLabel,
      studyIdForIsf: moduleCtx.studyIdForIsf,
      templateCategoryForIsf: moduleCtx.templateCategoryForIsf,
      title: title.trim(),
      description: description.trim(),
      sourceType: sourceType?.value,
      sourceRef: sourceRef.trim(),
      risk: riskLevel?.value,
    };

    saveCapa(payload, userData)
      .then(() => {
        resetCreateForm();
        if (isModal) {
          onSaved?.();
        } else {
          showAlert({
            title: "Success",
            message: "Record has been saved successfully.",
            type: "success",
          });
        }
      })
      .catch((err) => {
        showAlert({
          title: "Save failed",
          message: err?.message || "Unable to save CAPA. Please try again.",
          type: "danger",
        });
      });
  };

  const handleCancel = () => {
    if (isModal) {
      onCancel?.();
    } else {
      window.history.back();
    }
  };

  if (!userData) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: isModal ? 120 : 200 }}
      >
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!canCreateCapa) {
    return (
      <div className="alert alert-warning mb-0">
        You do not have permission to create CAPA.
      </div>
    );
  }

  const formBody = (
    <>
      <div className="row g-3">
        <div className="col-12">
          <label className="form-label">
            Title <span className="text-danger">*</span>
          </label>
          <input
            type="text"
            className="form-control"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={500}
          />
        </div>
        <div className="col-12">
          <label className="form-label">
            Description <span className="text-danger">*</span>
          </label>
          <TextEditor
            value={description}
            onChange={setDescription}
            placeholder="Enter description..."
            height={isModal ? "10rem" : "14rem"}
            minHeight={isModal ? "8rem" : "12rem"}
            className="capa-create-description-editor"
            showImageButton={false}
          />
        </div>
      </div>

      <div className="row g-3 mt-3">
        <div className="col-md-4">
          <label className="form-label">Module</label>
          <Dropdown
            options={moduleOptions}
            selected={moduleOption}
            onSelect={handleModuleChange}
            disabled={contextLocked}
          />
        </div>

        {entityUiMode === "study" && (
          <div className="col-md-8">
            <label className="form-label">
              Study <span className="text-danger">*</span>
              {!contextLocked && loadingStudies && (
                <span className="ms-2 text-muted small">Loading...</span>
              )}
            </label>
            <Dropdown
              options={contextLocked && selectedStudy ? [selectedStudy] : studies}
              selected={selectedStudy}
              onSelect={setSelectedStudy}
              disabled={contextLocked}
            />
          </div>
        )}

        {entityUiMode === "staff" && (
          <div className="col-md-8">
            <label className="form-label">
              Staff <span className="text-danger">*</span>
              {!contextLocked && loadingStaff && (
                <span className="ms-2 text-muted small">Loading...</span>
              )}
            </label>
            <Dropdown
              options={contextLocked && selectedStaff ? [selectedStaff] : staffOptions}
              selected={selectedStaff}
              onSelect={setSelectedStaff}
              disabled={contextLocked}
            />
          </div>
        )}

        {entityUiMode === "isf" && (
          <>
            <div className="col-md-4">
              <label className="form-label">
                Study <span className="text-danger">*</span>
              </label>
              <Dropdown
                options={contextLocked && isfStudy ? [isfStudy] : studies}
                selected={isfStudy}
                onSelect={(opt) => {
                  if (contextLocked) return;
                  setIsfStudy(opt);
                  setSelectedDocument(null);
                }}
                disabled={contextLocked}
              />
            </div>
            <div className="col-md-4">
              <label className="form-label">
                ISF category <span className="text-danger">*</span>
              </label>
              <Dropdown
                options={CAPA_ISF_TEMPLATE_CATEGORIES}
                selected={isfCategory}
                onSelect={(opt) => {
                  if (contextLocked) return;
                  setIsfCategory(opt);
                  setSelectedDocument(null);
                }}
                disabled={contextLocked}
              />
            </div>
            <div className="col-md-8">
              <label className="form-label">
                Document <span className="text-danger">*</span>
                {!contextLocked && loadingDocs && (
                  <span className="ms-2 text-muted small">Loading...</span>
                )}
              </label>
              <Dropdown
                options={
                  contextLocked && selectedDocument
                    ? [selectedDocument]
                    : documentOptions
                }
                selected={selectedDocument}
                onSelect={setSelectedDocument}
                disabled={contextLocked}
              />
            </div>
          </>
        )}
      </div>

      <div className="row g-3 mt-3">
        <div className="col-md-4">
          <label className="form-label">
            Source type <span className="text-danger">*</span>
          </label>
          <Dropdown
            options={sourceTypeOptions}
            selected={sourceType}
            onSelect={setSourceType}
          />
        </div>
        <div className="col-md-8">
          <label className="form-label">Source reference</label>
          <input
            type="text"
            className="form-control"
            value={sourceRef}
            onChange={(e) => setSourceRef(e.target.value)}
            placeholder="Optional external reference"
          />
        </div>
        <div className="col-12">
          <label className="form-label">Risk level</label>
          <div
            className="capa-risk-level"
            role="radiogroup"
            aria-label="Risk level"
          >
            {CAPA_RISK_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                className={`capa-risk-pill capa-risk-pill--${opt.value.toLowerCase()} ${
                  riskLevel?.value === opt.value ? "is-selected" : ""
                }`}
                onClick={() => setRiskLevel(opt)}
                aria-pressed={riskLevel?.value === opt.value}
              >
                <span className="capa-risk-pill__dot" aria-hidden />
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className={`${isModal ? "mt-3" : "mt-4"} d-flex justify-content-end gap-2`}>
        <button
          type="button"
          className="btn btn-outline-secondary"
          onClick={handleCancel}
        >
          {isModal ? "Cancel" : "Back"}
        </button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleSave}
        >
          Save CAPA
        </button>
      </div>
    </>
  );

  if (isModal) {
    return (
      <div className="capa-create-page capa-create-form--modal">
        {formBody}
        <InfoAlert
          isOpen={alertState.isOpen}
          onClose={hideAlert}
          {...alertState}
        />
      </div>
    );
  }

  return (
    <div className="capa-create-page">
      <div className="card shadow-sm mt-3">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Create CAPA</h5>
          {contextLocked && (
            <span className="badge bg-secondary">Context locked (from link)</span>
          )}
        </div>
        <div className="card-body">{formBody}</div>
      </div>
      <InfoAlert
        isOpen={alertState.isOpen}
        onClose={hideAlert}
        {...alertState}
      />
    </div>
  );
};

export default CapaCreateForm;
